import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Text, Loader } from '@Sage/hoi-poi-ui';
import { EmptyScreen } from '@Sage/ssm-web5';
import { subscribe } from 'lib/EventBuser';
import NoData from 'components/SvgIcons/emptyScreen/NoData';
import { getLiteral } from 'utils/getLiteral';
import { listDomains } from 'services/DomainService';
import DomainList from './DomainList';
import CreateDomainModal from './CreateDomainModal';
import { OPEN_CREATE_DOMAIN_MODAL } from './constants';

import './styles.scss';

function DomainsSettings() {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const fetchDomains = useCallback(() => {
        setLoading(true);
        return listDomains()
            .then((data) => {
                setDomains(data);
            })
            .catch((error) => {
                console.error('Error fetching domains:', error);
                setDomains([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    const handleOpenCreateModal = useCallback(() => {
        setIsCreateModalOpen(true);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
        setIsCreateModalOpen(false);
    }, []);

    const handleCreateSuccess = useCallback(() => {
        fetchDomains();
        handleCloseCreateModal();
    }, [fetchDomains, handleCloseCreateModal]);

    useEffect(() => {
        const unsubscribe = subscribe(OPEN_CREATE_DOMAIN_MODAL, handleOpenCreateModal);
        return unsubscribe;
    }, [handleOpenCreateModal]);

    const hasDomains = useMemo(() => {
        return domains && domains.length > 0;
    }, [domains]);

    const getEmptyState = () => (
        <EmptyScreen
            title={getLiteral('label_marketing_campaigns_no_domains_title')}
            description={getLiteral('label_marketing_campaigns_no_domains_desc')}
            illustration={<NoData width={168} />}
            buttons={[
                <Button key="add-domain" onClick={handleOpenCreateModal} type="secondary">
                    {getLiteral('action_marketing_campaigns_add_domain')}
                </Button>,
            ]}
        />
    );

    const getDomainsContent = () => (
        <>
            <Text type="body" className="marketing-settings__domains-description">
                {getLiteral('label_marketing_campaigns_domain_description')}
            </Text>
            <Text type="h5" className="marketing-settings__domains-table-title">
                {getLiteral('label_marketing_campaigns_domain_information')}
            </Text>
            <DomainList
                domains={domains}
                onDomainDeleted={fetchDomains}
                onDomainUpdated={fetchDomains}
            />
        </>
    );

    if (loading) {
        return (
            <div className="marketing-settings__domains-loading">
                <Loader size="large" />
            </div>
        );
    }

    return (
        <div className="marketing-settings__domains">
            <div className="marketing-settings__domains-scroll-container">
                {hasDomains ? getDomainsContent() : getEmptyState()}
            </div>

            <CreateDomainModal
                isOpen={isCreateModalOpen}
                onCancel={handleCloseCreateModal}
                onRequestClose={handleCloseCreateModal}
                onSuccess={handleCreateSuccess}
            />
        </div>
    );
}

DomainsSettings.displayName = 'DomainsSettings';

export default DomainsSettings;
